import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { validateRedirectUrl } from '../common/utils/utils';

interface LoginFormProps {
  onToggleMode: () => void;
  onForgotPassword: () => void;
  redirect?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode, onForgotPassword, redirect }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { user, error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      setError(error.message || 'Failed to sign in.');
    } else if (user) {
      const validRedirect = validateRedirectUrl(redirect);
      navigate(validRedirect || '/');
    }
  };

  return (
    <>
      {/* Logo */}
      <div className="mb-10">
        <img src="/logo_horizontal.png" alt="NoteWell" className="h-8 w-auto" />
      </div>

      <h1 className="text-3xl font-semibold">Welcome back</h1>
      {/* <p className="mt-2 text-sm text-zinc-500">Sign in to your account</p> */}

      {/* OAuth - commented out for now */}
      {/* <div className="mt-8 space-y-3">
        <button
          type="button"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-zinc-300 bg-white py-2.5 text-sm hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <SquareIcon />
          Continue with GitHub
        </button>

        <button
          type="button"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-zinc-300 bg-white py-2.5 text-sm hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <LockIcon />
          Continue with SSO
        </button>
      </div>

      <div className="my-6 flex items-center gap-4 text-zinc-400">
        <div className="h-px flex-1 bg-zinc-200" />
        <span className="text-xs uppercase tracking-wider">or</span>
        <div className="h-px flex-1 bg-zinc-200" />
      </div> */}

      {/* Email/password */}
      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        {error && (
          <div className="text-red-500 text-sm text-center mb-2">
            {error.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        )}
        <div>
          <label htmlFor="email" className="block text-sm mb-1">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="password" className="block text-sm">
              Password
            </label>
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-xs text-zinc-500 hover:text-zinc-700"
            >
              Forgot Password?
            </button>
          </div>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="••••••••"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-md bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>

      {/* Switch */}
      <p className="mt-6 text-center text-sm text-zinc-500">
        Don&apos;t have an account?{' '}
        <button type="button" onClick={onToggleMode} className="text-zinc-900 hover:underline">
          Sign Up Now
        </button>
      </p>

      {/* Legal */}
      <p className="mt-10 text-[11px] leading-5 text-zinc-400 text-center">
        By continuing, you agree to Notewell&apos;s{' '}
        <a
          href="https://www.notewell.ai/terms-of-service"
          className="underline hover:text-zinc-700"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://www.notewell.ai/privacy-policy" className="underline hover:text-zinc-700">
          Privacy Policy
        </a>
        , and to receive periodic emails with updates.
      </p>
    </>
  );
};

// ------------------------
// ICONS
// ------------------------
const SquareIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.5" y="1.5" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const LockIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5.5 7V5.5C5.5 3.6 7 2 8.8 2C10.6 2 12.1 3.6 12.1 5.5V7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export default LoginForm;
